import { useCallback, useEffect, useRef, useState } from 'react';
import { adminService } from '../services/admin.service';

const LIST_POLL_MS = 30000;
const DRIVER_POLL_MS = 15000;

function normalizeJob(raw) {
    return {
        id: raw.id,
        case_id: raw.case_id,
        service_number: raw.service_number,
        status: raw.status,
        pickup_location: raw.pickup_location,
        drop_location: raw.drop_location,
        created_at: raw.created_at,
        updated_at: raw.updated_at || raw.created_at,
        customer_name: raw.customer_name || raw.case?.customer?.name || 'N/A',
        vendor_name:
            raw.vendor_name ||
            raw.vendor_location?.dealer?.dealer_name ||
            raw.vendor_location?.vendor?.name ||
            'N/A',
        driver_id: raw.driver_id || raw.driver?.id || null,
        driver_name: raw.driver_name || raw.driver?.name || '—',
        completion_otp_escalated: !!(
            raw.completion_otp_escalated || raw.meta_data?.completion_otp_escalated
        ),
        driver_location: raw.driver_location || null,
        sla_state: raw.sla_state || 'ok',
        sla_phase: raw.sla_phase || null,
        sla_elapsed_sec: raw.sla_elapsed_sec ?? null,
        sla_target_sec: raw.sla_target_sec ?? null,
        sla_due_at: raw.sla_due_at || null,
    };
}

export function useLiveJobs({ enabled = true, autoRefresh = true } = {}) {
    const [jobs, setJobs] = useState([]);
    const [driverLocations, setDriverLocations] = useState([]);
    const [statusCatalog, setStatusCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const listTimerRef = useRef(null);
    const driverTimerRef = useRef(null);

    const fetchLiveJobs = useCallback(async ({ showLoading = false } = {}) => {
        if (!enabled) return;
        if (showLoading) setLoading(true);
        setError('');
        try {
            const data = await adminService.getLiveJobs();
            const catalog = data?.status_catalog || [];
            const rawJobs = data?.jobs || [];
            const drivers = data?.driver_locations || [];

            setStatusCatalog(catalog);
            setDriverLocations(drivers);
            setJobs(rawJobs.map(normalizeJob));
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Live jobs fetch failed:', err);
            const detail = err?.errorData?.detail || err?.message || 'Failed to load live jobs';
            setError(typeof detail === 'string' ? detail : 'Failed to load live jobs');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [enabled]);

    const refresh = useCallback(() => fetchLiveJobs({ showLoading: true }), [fetchLiveJobs]);

    useEffect(() => {
        if (!enabled) return undefined;

        fetchLiveJobs({ showLoading: true });

        if (!autoRefresh) return undefined;

        listTimerRef.current = setInterval(() => {
            fetchLiveJobs({ showLoading: false });
        }, LIST_POLL_MS);

        return () => {
            if (listTimerRef.current) clearInterval(listTimerRef.current);
        };
    }, [enabled, autoRefresh, fetchLiveJobs]);

    useEffect(() => {
        if (!enabled || !autoRefresh) return undefined;

        driverTimerRef.current = setInterval(() => {
            adminService
                .getDriverLocations()
                .then((drivers) => {
                    setDriverLocations(drivers || []);
                    setLastUpdated(new Date());
                })
                .catch((err) => console.warn('Driver location poll failed:', err));
        }, DRIVER_POLL_MS);

        return () => {
            if (driverTimerRef.current) clearInterval(driverTimerRef.current);
        };
    }, [enabled, autoRefresh]);

    const jobsWithDrivers = jobs.map((job) => {
        const driverEntry = driverLocations.find(
            (d) =>
                d?.driver?.id === job.driver_id ||
                d?.current_service?.id === job.id ||
                d?.current_service_id === job.id,
        );
        const loc = driverEntry?.location;
        return {
            ...job,
            driver_location: loc
                ? {
                      latitude: Number(loc.latitude),
                      longitude: Number(loc.longitude),
                      recorded_at: loc.recorded_at,
                  }
                : job.driver_location,
        };
    });

    return {
        jobs: jobsWithDrivers,
        driverLocations,
        statusCatalog,
        loading,
        error,
        lastUpdated,
        refresh,
    };
}

export default useLiveJobs;
